import { Engine } from './core/engine.js';
import { createConfig } from './core/config.js';

import { RenderSystem } from './render/index.js';
import { MaterialSystem } from './materials/index.js';
import { SkySystem } from './sky/index.js';
import { WorldSystem } from './world/index.js';
import { PhysicsSystem } from './physics/index.js';
import { PlayerSystem } from './player/index.js';
import { WeaponSystem } from './weapons/index.js';
import { FxSystem } from './fx/index.js';
import { AiSystem } from './ai/index.js';
import { UiSystem } from './ui/index.js';
import { AudioSystem } from './audio/index.js';

import { installShotApi } from './dev/shots.js';
import { prewarm } from './core/prewarm.js';

const params = new URLSearchParams(location.search);
const capture = params.get('capture') === '1';
const QUALITY_KEY = 'overwatch.graphicsQuality';
const RECOVERY_KEY = 'overwatch.graphicsRecovery';
const validQuality = (value) => ['low', 'medium', 'high', 'ultra'].includes(value);

function readStorage(storage, key) {
  try { return storage.getItem(key); } catch { return null; }
}

function writeStorage(storage, key, value) {
  try { storage.setItem(key, value); } catch { /* privacy mode */ }
}

function chooseQuality() {
  const explicit = params.get('q');
  if (validQuality(explicit)) return { quality: explicit, reason: 'URL override' };

  // Screenshot/capture tooling historically omits q and expects the ultra
  // reference pipeline. Interactive hardware detection must not move its gate.
  if (capture) return { quality: 'ultra', reason: 'deterministic capture default' };

  if (readStorage(sessionStorage, RECOVERY_KEY) === '1') {
    return { quality: 'low', reason: 'graphics recovery' };
  }

  const saved = readStorage(localStorage, QUALITY_KEY);
  if (validQuality(saved)) return { quality: saved, reason: 'saved setting' };

  const cores = navigator.hardwareConcurrency || 0;
  const memory = navigator.deviceMemory || 0;
  const lowSpec = (cores > 0 && cores <= 4) || (memory > 0 && memory <= 4);
  return lowSpec
    ? { quality: 'low', reason: `${cores || '?'} CPU threads / ${memory || '?'} GB reported memory` }
    : { quality: 'medium', reason: 'balanced first-run default' };
}

function createBootOverlay(quality, reason) {
  const root = document.createElement('div');
  root.id = 'boot-status';
  root.setAttribute('role', 'status');
  root.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:10000', 'display:grid', 'place-items:center',
    'pointer-events:none', 'background:radial-gradient(circle at 50% 44%,#14212a 0,#070b0f 55%,#020304 100%)',
    'color:#eef8ff', 'font:600 13px/1.55 Inter,Arial,sans-serif', 'letter-spacing:.08em',
  ].join(';');
  root.innerHTML = `<div style="width:min(430px,82vw);text-align:center">
    <div style="font-size:23px;letter-spacing:.22em;margin-bottom:16px">OVERWATCH</div>
    <div data-boot-message>PREPARING ${quality.toUpperCase()} GRAPHICS</div>
    <div style="height:3px;background:#20303a;margin:15px 0 10px;overflow:hidden">
      <div data-boot-bar style="width:3%;height:100%;background:#69d6ff;transition:width .2s"></div>
    </div>
    <div style="color:#7f9ba9;font-size:10px;font-weight:500;letter-spacing:.04em">${reason}</div>
  </div>`;
  document.body.appendChild(root);
  return {
    update(message, progress) {
      const label = root.querySelector('[data-boot-message]');
      const bar = root.querySelector('[data-boot-bar]');
      if (label) label.textContent = message;
      if (bar) bar.style.width = `${Math.max(3, Math.min(100, progress * 100))}%`;
    },
    fail(title, detail, { recovery = false } = {}) {
      root.style.display = 'grid';
      root.style.pointerEvents = 'auto';
      root.innerHTML = `<div style="width:min(520px,86vw);text-align:center">
        <div data-boot-title style="font-size:20px;margin-bottom:12px"></div>
        <div data-boot-detail style="color:#b4c6cf;font-weight:400;letter-spacing:.02em;white-space:pre-wrap"></div>
        ${recovery ? '<button data-safe-reload style="margin-top:20px;padding:10px 18px;border:1px solid #69d6ff;background:#0d2834;color:#e9faff;cursor:pointer">RELOAD IN SAFE MODE</button>' : ''}
      </div>`;
      root.querySelector('[data-boot-title]').textContent = String(title);
      root.querySelector('[data-boot-detail]').textContent = String(detail);
      root.querySelector('[data-safe-reload]')?.addEventListener('click', () => {
        const url = new URL(location.href);
        url.searchParams.set('q', 'low');
        url.searchParams.set('prewarm', '0');
        location.replace(url);
      });
    },
    hide() { root.style.display = 'none'; },
  };
}

async function bootGame() {
const startup = chooseQuality();
const boot = createBootOverlay(startup.quality, startup.reason);
await new Promise((resolve) => requestAnimationFrame(resolve));
// Deterministic shutter for the pixel gate: the engine does not schedule its own
// frames, the driver advances exactly N of them through window.__PUMP__. Opt-in,
// because tools that measure real frame pacing (tools/perf.mjs) need the loop to
// free-run. See the long comment in src/dev/shots.js.
const lockstep = capture && params.get('lockstep') === '1';

const config = createConfig({
  quality: startup.quality,
  deterministic: capture,
  // Captures retain their proven prewarm path. During normal play it is opt-in:
  // on a Vega 3 the same compile storm took over five minutes before frame one.
  prewarmShaders: params.get('prewarm') === '1' || (capture && params.get('prewarm') !== '0'),
});

const canvas = document.getElementById('game');

const engine = new Engine({ canvas, config });
let contextLost = false;
canvas.addEventListener('webglcontextlost', (event) => {
  event.preventDefault();
  if (contextLost) return;
  contextLost = true;
  engine.stop();
  writeStorage(sessionStorage, RECOVERY_KEY, '1');
  document.getElementById('boot-status').style.display = 'grid';
  boot.fail(
    'GRAPHICS DRIVER RESET',
    'The WebGL context was lost. Gameplay has stopped to avoid another driver crash.',
    { recovery: true }
  );
});
canvas.addEventListener('webglcontextrestored', () => {
  // Custom MRT, shadow and history targets are not safe to reuse after a reset;
  // rebuilding the page is deterministic and honours the recovery flag above.
  if (contextLost) {
    const url = new URL(location.href);
    url.searchParams.set('q', 'low');
    url.searchParams.set('prewarm', '0');
    location.replace(url);
  }
});
engine.events.on('engine:error', ({ error }) => {
  writeStorage(sessionStorage, RECOVERY_KEY, '1');
  boot.fail(
    'RENDERING STOPPED',
    error?.stack ?? error?.message ?? String(error),
    { recovery: config.quality !== 'low' }
  );
});

// Registration order is irrelevant — Registry topo-sorts on static deps.
engine
  .add(RenderSystem)
  .add(MaterialSystem)
  .add(SkySystem)
  .add(WorldSystem)
  .add(PhysicsSystem)
  .add(PlayerSystem)
  .add(WeaponSystem)
  .add(FxSystem);

// Compatibility mode prioritises getting a playable frame onto weak hardware.
// The AI garrison owns its own texture forge, three soldier builds and navmesh;
// leaving it out saves another large boot/compile burst. Higher presets and all
// deterministic captures retain the full game.
if (config.q.ai !== false) engine.add(AiSystem);

engine.add(UiSystem).add(AudioSystem);

try {
  await engine.init({
    onProgress: async ({ id, index, total }) => {
      boot.update(`LOADING ${id.toUpperCase()}`, index / Math.max(1, total + 1));
      // Let the loading screen paint between expensive subsystem initializers.
      await new Promise((resolve) => requestAnimationFrame(resolve));
    },
  });
} catch (err) {
  console.error('[boot] init failed', err);
  boot.fail('BOOT FAILURE', err.stack ?? err.message, { recovery: config.quality !== 'low' });
  throw err;
}

const shotApi = installShotApi(engine, { capture, lockstep });

// Compile every shader permutation before the frame loop starts. Measured: without
// this, 86 programs compile lazily during play, up to 30 on one frame, producing
// 3.1-3.9 SECOND stalls. See src/core/prewarm.js.
//
// ON BY DEFAULT for deterministic capture; interactive play opts in with
// `?prewarm=1`. It is PROVEN pixel-neutral: `tools/baseline.mjs` with
// `--query=prewarm=0` vs `--query=prewarm=1` reports identical:true on all 11
// shots (0 changed pixels, maxDelta 0). The two things that previously made the
// ~1.4 s pre-warm spend look like a visual change were both boot-duration
// couplings OUTSIDE the subsystems: (1) the shutter frame index was latency-bound
// because the engine kept stepping through the driver's round trips — fixed by
// lockstep in src/dev/shots.js; (2) `will-change: transform` on the compass strip
// cached a composited-layer raster taken at a wall-clock-dependent moment — fixed
// in src/ui/style.js.
boot.update(config.prewarmShaders ? 'OPTIMIZING SHADERS' : 'STARTING MISSION', 0.94);
const warmup = config.prewarmShaders
  ? await prewarm(engine)
  : { ok: false, reason: 'interactive prewarm disabled' };
console.info('[boot] prewarm', warmup);
window.__PREWARM__ = warmup;

engine.start();

// Capture harness handshake: only flag ready once a frame has actually landed.
//
// BOOT_FRAMES is deliberately a frame COUNT, not a rAF race. In lockstep mode the
// engine has no loop of its own, so we hand-pump exactly this many frames and only
// then raise __READY__; the shot is therefore always applied at engine frame 3, no
// matter how long boot (or pre-warm) took in wall-clock terms.
const BOOT_FRAMES = 3;
if (lockstep) {
  await shotApi.pump(BOOT_FRAMES);
  window.__READY__ = true;
} else {
  let warm = 0;
  const readyProbe = () => {
    if (++warm >= BOOT_FRAMES) {
      window.__READY__ = true;
      writeStorage(sessionStorage, RECOVERY_KEY, '0');
      boot.hide();
      return;
    }
    requestAnimationFrame(readyProbe);
  };
  requestAnimationFrame(readyProbe);
}

if (lockstep) {
  writeStorage(sessionStorage, RECOVERY_KEY, '0');
  boot.hide();
}

window.__ENGINE__ = engine;

if (import.meta.hot) {
  import.meta.hot.dispose(() => engine.dispose());
}
}

void bootGame().catch((err) => console.error('[boot] fatal', err));
